// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import FilterAutocomplete from "components/FilterAutocomplete";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/MUI";

// Data
import axios from "axios";
import { withAuthContext } from "context/Auth";
import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import MDBadge from "components/MDBadge";
import { useNavigate } from "react-router-dom";
import swal from "sweetalert";
import moment from "moment";
import { useCappedRetry } from "utils/retry";

const VISITOR_FILTER_INITIAL_STATE = { tower: "", apartment: "", visitPurpose: "", visiting: "", admin: "" };
const VISITOR_FILTER_OPTIONS_INITIAL = { towers: [], apartments: [], visitPurposes: [], visiting: [], admins: [] };

function Tables({ Token, CheckToken, AdminRole }) {
	const navigate = useNavigate();
	const [data, setData] = useState([]);
	const [error, setError] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const isFetchingRef = useRef(false);
	const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 10, totalPages: 0 });
	const [currentPage, setCurrentPage] = useState(1);
	const [itemsPerPage, setItemsPerPage] = useState(10);
	const [filters, setFilters] = useState(() => ({ ...VISITOR_FILTER_INITIAL_STATE }));
	const [filterOptions, setFilterOptions] = useState(() => ({ ...VISITOR_FILTER_OPTIONS_INITIAL }));
	const [isFilterLoading, setIsFilterLoading] = useState(false);
	const [filterError, setFilterError] = useState("");
	const retryGetVisitors = useCappedRetry();
	const retryFetchFilters = useCappedRetry();
	const retryChangeTime = useCappedRetry();
	const towerOptions = useMemo(() =>
		(filterOptions?.towers || []).map((tower) => {
			const optionValue = tower?._id || tower?.value || tower?.Tower || tower?.TowerNo || tower;
			const optionLabel = tower?.name || tower?.label || tower?.Tower || tower?.TowerNo || optionValue;
			return {
				label: optionLabel || "Unknown",
				value: optionValue || ""
			};
		}),
	[filterOptions?.towers]
	);
	const apartmentOptions = useMemo(() =>
		(filterOptions?.apartments || []).map((apartment) => {
			const optionValue = apartment?._id || apartment?.value || apartment;
			const apartmentLabel = apartment?.AppratmentNo || apartment?.ApartmentNo || apartment?.FlatNo;
			const towerLabel = apartment?.Tower || apartment?.TowerNo || "";
			const optionLabel = [towerLabel, apartmentLabel || apartment?.name || apartment?.label || optionValue]
				.filter(Boolean)
				.join(" - ");
			return {
				label: optionLabel || "Unknown",
				value: optionValue || ""
			};
		}),
	[filterOptions?.apartments]
	);
	const visitPurposeOptions = useMemo(() =>
		(filterOptions?.visitPurposes || []).map((purpose) => {
			const optionValue = purpose?._id || purpose?.value || purpose;
			const optionLabel = purpose?.name || purpose?.label || purpose?.value || purpose;
			return {
				label: optionLabel || "Unknown",
				value: optionValue || ""
			};
		}),
	[filterOptions?.visitPurposes]
	);
	const visitingOptions = useMemo(() =>
		(filterOptions?.visiting || []).map((visitor) => {
			const optionValue = visitor?._id || visitor?.value || visitor?.name || visitor;
			const optionLabel = visitor?.name || visitor?.label || visitor?.value || visitor;
			return {
				label: optionLabel || "Unknown",
				value: optionValue || ""
			};
		}),
	[filterOptions?.visiting]
	);
	const adminOptions = useMemo(() =>
		(filterOptions?.admins || []).map((admin) => {
			const optionValue = admin?._id || admin?.value || admin?.email || admin;
			const optionLabel = admin?.name || admin?.label || admin?.email || optionValue;
			return {
				label: optionLabel || "Unknown",
				value: optionValue || ""
			};
		}),
	[filterOptions?.admins]
	);

	const GetVisitors = useCallback(() => {
		if (isFetchingRef.current) return;

		if (Token || localStorage.getItem("token")) {
			isFetchingRef.current = true;
			setIsLoading(true);
			const params = {
				page: currentPage,
				limit: itemsPerPage
			};
			if (filters.tower) params.tower = filters.tower;
			if (filters.apartment) params.apartment = filters.apartment;
			if (filters.visitPurpose) params.visitPurpose = filters.visitPurpose;
			if (filters.visiting) params.visiting = filters.visiting;
			if (filters.admin) params.admin = filters.admin;

			axios
				.get(`${process.env.REACT_APP_PUBLIC_PATH}/GetAllVisitors`, {
					params,
					headers: {
						Authorization: `Bearer ${Token || localStorage.getItem("accessToken") || localStorage.getItem("token")}`
					}
				})
				.then((res) => {
					if (res?.data?.status == 200) {
						setData(res?.data?.data);
						if (res?.data?.pagination) {
							setPagination(res?.data?.pagination);
						}
					} else {
						setError(res?.data?.message);
					}
				})
				.catch((err) => {
					setError(err?.message);
				})
				.finally(() => {
					setIsLoading(false);
					isFetchingRef.current = false;
				});
		} else {
			retryGetVisitors(() => {
				CheckToken();
				GetVisitors();
			});
		}
	}, [Token, CheckToken, currentPage, itemsPerPage, filters]);

	const handleFilterChange = useCallback((field, value) => {
		setCurrentPage(1);
		setFilters((prev) => ({
			...prev,
			[field]: value || ""
		}));
	}, []);

	const handleClearFilters = useCallback(() => {
		setCurrentPage(1);
		setFilters({ ...VISITOR_FILTER_INITIAL_STATE });
	}, []);

	const fetchVisitorFilters = useCallback(() => {
		const tokenHeader = Token || localStorage.getItem("accessToken") || localStorage.getItem("token");
		if (!tokenHeader) {
			retryFetchFilters(() => {
				CheckToken();
				fetchVisitorFilters();
			});
			return;
		}
		setIsFilterLoading(true);
		setFilterError("");
		axios
			.get(`${process.env.REACT_APP_PUBLIC_PATH}/GetVisitorFilters`, {
				headers: { Authorization: `Bearer ${tokenHeader}` }
			})
			.then((res) => {
				if (res?.data?.status === 200) {
					const payload = res?.data?.data || {};
					setFilterOptions({
						towers: payload?.towers || [],
						apartments: payload?.apartments || [],
						visitPurposes: payload?.visitPurposes || [],
						visiting: payload?.visiting || [],
						admins: payload?.admins || []
					});
				} else {
					setFilterError(res?.data?.message || "Unable to load filters");
				}
			})
			.catch((err) => {
				setFilterError(err?.response?.data?.message || err?.message || "Unable to load filters");
			})
			.finally(() => setIsFilterLoading(false));
	}, [Token, CheckToken]);

	const handleChangeTime = useCallback(({ id }) => {
		if (Token) {
			axios
				.post(`${process.env.REACT_APP_PUBLIC_PATH}/Change-ExitTime-Visitor`, {
					id: id,
					ExitTime: new Date()
				}, {
					headers: {
						Authorization: `Bearer ${Token || localStorage.getItem("accessToken") || localStorage.getItem("token")}`
					}
				})
				.then((res) => {
					GetVisitors();
					swal({
						text: res?.data?.message,
						button: {
							text: "Ok",
							closeModal: true
						},
						icon: res?.data?.status == 200 ? "success" : "error",
						time: 3000
					});
				})
				.catch((err) => {
					swal({
						text: err?.response?.data?.message
							? err?.response?.data?.message
							: "There was some Error",
						button: {
							text: "Ok",
							closeModal: true
						},
						icon: "error",
						time: 3000
					});
				});
		} else {
			retryChangeTime(() => {
				CheckToken();
				handleChangeTime({ id });
			});
		}
	}, [Token, CheckToken, GetVisitors]);

	const columns = useMemo(() => [
		{
			headerName: "Action",
			field: "action",
			width: 200,
			renderCell: ({ row: a }) => (!a?.ExitTime || a?.ExitTime == "") && (
				<MDBox>
					<MDBadge
						badgeContent={"Visitor Left"}
						color={"dark"}
						variant="gradient"
						size="sm"
						onClick={() => {
							handleChangeTime({ id: a?._id });
						}}
					/>
				</MDBox>
			)
		},
		{ headerName: "Name", field: "Name" },
		{ headerName: "TowerNo", field: "TowerNo" },
		{ headerName: "AppratmentNo", field: "AppratmentNo" },
		{ headerName: "VehicleNumber", field: "VehicleNumber" },
		{ headerName: "VisitPurpose", field: "VisitPurpose" },
		{
			headerName: "EntryTime", field: "EntryTime", width: 200, renderCell: ({ row: a }) => (
				<MDBox>
					{moment(a?.EntryTime).format("hh:mm a  DD/MM/YYYY")}
				</MDBox>
			)
		},
		{
			headerName: "ExitTime", field: "ExitTime", width: 200, renderCell: ({ row: a }) => (
				<MDBox>
					{a?.ExitTime ? moment(a.ExitTime).format("hh:mm a DD/MM/YYYY") : "-"}
				</MDBox>
			)
		},
		{ headerName: "Visiting", field: "Visiting" },
		{ headerName: "ContactNumber", field: "ContactNumber" },
		{
			headerName: "Notes",
			field: "Notes"
		},
	], [handleChangeTime]);

	useEffect(() => {
		fetchVisitorFilters();
	}, [fetchVisitorFilters]);

	useEffect(() => {
		GetVisitors();
	}, [GetVisitors]);

	const handlePageChange = useCallback((newPage, newPageSize) => {
		if (newPageSize !== itemsPerPage) {
			setItemsPerPage(newPageSize);
			setCurrentPage(1);
		} else {
			setCurrentPage(newPage);
		}
	}, [itemsPerPage]);

	return (
		<DashboardLayout>
			<DashboardNavbar />
			<MDBox pt={6} pb={3}>
				<Grid container spacing={6}>
					<Grid item xs={12}>
						<Card>
							<MDBox
								mx={2}
								mt={-3}
								py={3}
								px={2}
								variant="gradient"
								bgColor="info"
								borderRadius="lg"
								coloredShadow="info"
							>
								<Grid container style={{ width: "100%" }}>
									<Grid item xs={6}>
										<MDTypography variant="h6" color="white">
											Visitor
										</MDTypography>
									</Grid>
									<Grid
										item
										xs={6}
										style={{
											display: "flex",
											alignItems: "flex-end",
											alignContent: "flex-end",
											justifyContent: "flex-end"
										}}
									>
										<MDBadge
											badgeContent="New"
											color="dark"
											variant="gradient"
											size="sm"
											onClick={() => navigate(`/VisitorAction/New`)}
										/>
									</Grid>
								</Grid>
							</MDBox>

							<MDBox px={2} py={2} mt={2} bgcolor="rgba(255,255,255,0.8)" borderRadius="lg">
								<Grid container spacing={2}>
									<Grid item xs={12} sm={6} md={4}>
										<FilterAutocomplete
											name="tower"
											label="Tower"
											value={filters.tower}
											options={towerOptions}
											onChange={handleFilterChange}
											loading={isFilterLoading}
											disabled={isFilterLoading}
											placeholder="Search tower"
										/>
									</Grid>
									<Grid item xs={12} sm={6} md={4}>
										<FilterAutocomplete
											name="apartment"
											label="Apartment"
											value={filters.apartment}
											options={apartmentOptions}
											onChange={handleFilterChange}
											loading={isFilterLoading}
											disabled={isFilterLoading}
											placeholder="Search apartment"
										/>
									</Grid>
									<Grid item xs={12} sm={6} md={4}>
										<FilterAutocomplete
											name="visitPurpose"
											label="Visit Purpose"
											value={filters.visitPurpose}
											options={visitPurposeOptions}
											onChange={handleFilterChange}
											loading={isFilterLoading}
											disabled={isFilterLoading}
											placeholder="Search purpose"
										/>
									</Grid>
									<Grid item xs={12} sm={6} md={4}>
										<FilterAutocomplete
											name="visiting"
											label="Visiting"
											value={filters.visiting}
											options={visitingOptions}
											onChange={handleFilterChange}
											loading={isFilterLoading}
											disabled={isFilterLoading}
											placeholder="Search resident"
										/>
									</Grid>
									<Grid item xs={12} sm={6} md={4}>
										<FilterAutocomplete
											name="admin"
											label="Admin"
											value={filters.admin}
											options={adminOptions}
											onChange={handleFilterChange}
											loading={isFilterLoading}
											disabled={isFilterLoading}
											placeholder="Search admin"
										/>
									</Grid>
									<Grid item xs={12} display="flex" justifyContent="flex-end">
										<MDButton
											variant="outlined"
											color="info"
											size="small"
											disabled={isFilterLoading}
											onClick={handleClearFilters}
										>
											Clear filters
										</MDButton>
									</Grid>
								</Grid>
								{filterError && (
									<MDTypography variant="caption" color="error">
										{filterError}
									</MDTypography>
								)}
							</MDBox>

							<MDBox pt={3}>
								{isLoading ? (
									<MDBox lineHeight={1} textAlign="center" py={3}>
										<MDTypography display="block" variant="caption" color="text" fontWeight="medium" fontSize="16px">
											Loading...
										</MDTypography>
									</MDBox>
								) : data.length > 0 ? (
									<DataTable
										rows={data}
										columns={columns}
										paginationMode="server"
										serverPagination={{
											total: pagination?.total || 0,
											page: currentPage,
											limit: itemsPerPage,
											onPageChange: handlePageChange
										}}
									/>
								) : (
									<MDBox lineHeight={1} textAlign="center">
										<MDTypography
											display="block"
											variant="caption"
											color="text"
											fontWeight="medium"
											fontSize="20px"
										>
											{error ? error : " No Data Found"}
										</MDTypography>
									</MDBox>
								)}
							</MDBox>
						</Card>
					</Grid>
				</Grid>
			</MDBox>
		</DashboardLayout>
	);
}

export default withAuthContext((Tables));
